import React, { useContext, useMemo } from 'react';
import { t } from '@sm/intl';
import { Typography } from '@wds/typography';

import { COMPARISON_PACKAGES } from '~app/pages/Pricing/lib/constants';
import { Packages, PackageDisplayNames } from '~shared/constants/pricing';
import { PackageData, PACKAGE_PRICE_TYPE } from '~shared/types/pricing/package';
import { PackageHeaderProps } from '~pricing/components/Summary/types';

import usePackageHeaderStyles from './useStyles';
import packageDefs from '~pricing/lib/packages/packageDefs';
import PackageCta from '../PackageCta';
import PackagePrice from '~pricing/components/PackagePrice';
import PackageByline from '~pricing/components/PackageHeader/PackageByline';
import Ribbon from '~pricing/components/Ribbon';
import Grow2529Ribbon from '~pricing/experiments/grow_2529_forms/components/Ribbon';
import ToggleSwitch from '../ToggleSwitch';

import {
  calculateSavingsPercent,
  formatMonthlyCostInLocaleCurrency,
  formatCostInLocaleCurrency,
  getCoreCostFromSkus,
} from '~app/pages/Pricing/lib/lib-helpers';

import usePriceDisplay from '~pricing/hooks/usePriceDisplay';
import useStarterEnabled from '~app/pages/Pricing/hooks/useStarterEnabled';

import COPY from '~pricing/components/PackageHeader/copy';
import {
  Grow2529ExperimentPackages,
  Grow2529ExperimentPackagesPartial,
} from '~pricing/experiments/grow_2529_forms/constants';
import EduContext from '~shared/hooks/eduContext';
import { usePricingState } from '~shared/hooks/usePricingState';

export default function PackageHeader({
  packageName,
  comparisonSkuCosts,
  skuCosts,
  isSummaryPage = false,
  discountInfo,
}: Readonly<PackageHeaderProps>): JSX.Element {
  const { Header } = usePackageHeaderStyles({ isSummaryPage });
  const { isStarterEnabled } = useStarterEnabled();

  const comparisonPackageName = COMPARISON_PACKAGES[packageName];
  const {
    state: {
      packages: { allPackages: packageData },
    },
  } = usePricingState();
  const { headerCost, headerDiscountedCost, headerByline, headerUnit } = usePriceDisplay(
    packageName,
    skuCosts,
    discountInfo
  );

  const coreSeatCost = useMemo(() => getCoreCostFromSkus(skuCosts), [skuCosts]);

  const comparisonCoreSeatCost = useMemo(
    () => comparisonSkuCosts && getCoreCostFromSkus(comparisonSkuCosts),
    [comparisonSkuCosts]
  );

  const comparisonFormattedPrice = useMemo(
    () => comparisonCoreSeatCost && formatCostInLocaleCurrency(comparisonCoreSeatCost),
    [comparisonCoreSeatCost]
  ) as string;

  const comparisonFormattedMonthlyPrice = useMemo(
    () => comparisonCoreSeatCost && formatMonthlyCostInLocaleCurrency(comparisonCoreSeatCost),
    [comparisonCoreSeatCost]
  ) as string;

  const comparisonPackage = useMemo(
    () =>
      comparisonPackageName
        ? packageData?.find((packageObj: PackageData) => packageObj.packageName === comparisonPackageName)
        : null,
    [comparisonPackageName, packageData]
  );

  const comparisonSeatCost = useMemo(
    () => (comparisonPackage ? getCoreCostFromSkus(comparisonPackage?.skuCost) : null),
    [comparisonPackage]
  );

  const savingsPercentage = useMemo(
    () => (coreSeatCost && comparisonSeatCost ? calculateSavingsPercent(comparisonSeatCost, coreSeatCost) : 0),
    [comparisonSeatCost, coreSeatCost]
  );

  const getRibbon = useMemo(() => {
    let ribbonText = packageDefs[packageName]?.highlighted ? t(COPY.BEST_VALUE) : null;

    if (savingsPercentage > 0) {
      ribbonText = t(COPY.SAVE_PERCENTAGE, { percentage: savingsPercentage });
    }

    if (ribbonText) {
      return packageName === Packages.AdvantageAnnual ? (
        <Grow2529Ribbon packageName={packageName}>{ribbonText}</Grow2529Ribbon>
      ) : (
        <Ribbon packageName={packageName}>{ribbonText}</Ribbon>
      );
    }
    return null;
  }, [packageName, savingsPercentage]);

  const { isEdu } = useContext(EduContext);

  return (
    <>
      {getRibbon}

      <header data-testid="PackageHeader__Header" className={Header}>
        {((!isEdu && isStarterEnabled && Grow2529ExperimentPackages.includes(packageName)) ||
          (!isEdu && !isStarterEnabled && Grow2529ExperimentPackagesPartial.includes(packageName))) && (
          <ToggleSwitch packageName={packageName} />
        )}

        <Typography component="h2" data-testid="PackageHeader__HeaderText" align="center">
          {t(
            packageName === Packages.StandardMonthly
              ? PackageDisplayNames.STARTER_MONTHLY
              : PackageDisplayNames[packageName]
          )}
        </Typography>
        {packageName !== Packages.Enterprise && (
          <PackagePrice
            packagePriceType={isSummaryPage ? PACKAGE_PRICE_TYPE.SUMMARY : PACKAGE_PRICE_TYPE.DETAIL}
            costUnit={headerUnit}
            formattedPrice={headerDiscountedCost ?? headerCost}
          />
        )}
        {![Packages.Enterprise, Packages.Basic].includes(packageName) && headerDiscountedCost && (
          <PackagePrice
            packagePriceType={PACKAGE_PRICE_TYPE.COMPARISON}
            costUnit={headerUnit}
            formattedPrice={headerCost}
          />
        )}
        {comparisonFormattedPrice && !headerDiscountedCost && (
          <PackagePrice
            packagePriceType={PACKAGE_PRICE_TYPE.COMPARISON}
            costUnit={headerUnit}
            formattedPrice={
              packageName === Packages.StandardMonthly ? comparisonFormattedPrice : comparisonFormattedMonthlyPrice
            }
          />
        )}

        <PackageByline byline={headerByline} />
        <PackageCta packageName={packageName} isSummaryPage={isSummaryPage} />
      </header>
    </>
  );
}
